import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import FormControl from "@material-ui/core/FormControl";
import InputAdornment from "@material-ui/core/InputAdornment";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import NewTicketModal from "../../components/NewTicketModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import NoContactChip from "../Opportunities/NoContactChip";

import { formatCurrency, formatDateOnly } from "../Opportunities/helpers";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  search: { minWidth: 260 },
  select: { minWidth: 190 },
  totals: {
    marginLeft: "auto",
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  totalValue: {
    fontWeight: "bold",
    color: "#C62828",
  },
  paper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  premium: {
    fontWeight: "bold",
  },
  products: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
  },
  rowChip: {
    marginLeft: 6,
  },
}));

const WINDOW_OPTIONS = [
  { value: "", label: "Todo o período" },
  { value: "30", label: "Saiu nos últimos 30 dias" },
  { value: "90", label: "Saiu nos últimos 90 dias" },
  { value: "180", label: "Saiu nos últimos 6 meses" },
  { value: "365", label: "Saiu no último ano" },
];

const ORDER_OPTIONS = [
  { value: "premiumDesc", label: "Maior prêmio perdido" },
  { value: "recentFirst", label: "Saiu há menos tempo" },
  { value: "oldestFirst", label: "Saiu há mais tempo" },
  { value: "clientAsc", label: "Nome do cliente" },
];

/**
 * Recuperação de Carteira: clientes sem nenhuma apólice vigente hoje.
 *
 * Complementa o Kanban em vez de repetir. O Kanban age antes do vencimento;
 * aqui estão os que já saíram. A lista vem de Policy, não de Opportunity, o
 * que faz duas diferenças práticas: funciona antes da conciliação com
 * contatos (quando ainda não existe card nenhum) e inclui quem venceu há
 * mais de 18 meses, que o Kanban não classifica.
 *
 * Ordenada por prêmio fora da carteira, porque é por onde a recuperação
 * compensa começar.
 */
const Recovery = () => {
  const classes = useStyles();
  const history = useHistory();

  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState({ clients: 0, lostPremium: 0, policies: 0 });
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const [searchParam, setSearchParam] = useState("");
  const [maxDays, setMaxDays] = useState("");
  const [order, setOrder] = useState("premiumDesc");

  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketContact, setTicketContact] = useState({});

  useEffect(() => {
    setPageNumber(1);
  }, [searchParam, maxDays, order]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/policies/lapsed-clients", {
        params: {
          searchParam,
          maxDaysSinceExpiration: maxDays || undefined,
          order,
          pageNumber,
          limit: 20,
        },
      });
      setClients((previous) =>
        pageNumber === 1 ? data.clients : [...previous, ...data.clients]
      );
      setSummary(data.summary);
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [searchParam, maxDays, order, pageNumber]);

  useEffect(() => {
    const debounce = setTimeout(fetchClients, 400);
    return () => clearTimeout(debounce);
  }, [fetchClients]);

  const handleOpenTicket = (client) => {
    if (!client.contactId) return;
    setTicketContact({
      id: client.contactId,
      name: client.contactName,
      number: client.contactNumber,
    });
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = (ticket) => {
    setTicketModalOpen(false);
    setTicketContact({});
    if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
  };

  const renderClientCell = (client) => {
    if (client.contactId && !client.contactPendingPhone) {
      return (
        <>
          {client.clientNameRaw}
          <Typography variant="caption" display="block" color="textSecondary">
            {client.contactNumber}
          </Typography>
        </>
      );
    }

    return (
      <>
        {client.clientNameRaw}
        <NoContactChip
          hasContact={Boolean(client.contactId)}
          className={classes.rowChip}
        />
      </>
    );
  };

  return (
    <MainContainer>
      <NewTicketModal
        modalOpen={ticketModalOpen}
        initialContact={ticketContact}
        onClose={handleCloseTicketModal}
      />

      <MainHeader>
        <Title>Recuperação de Carteira</Title>
      </MainHeader>

      <Paper className={classes.toolbar} variant="outlined">
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          placeholder="Buscar cliente ou CPF/CNPJ"
          value={searchParam}
          onChange={(event) => setSearchParam(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl className={classes.select} size="small" variant="outlined">
          <InputLabel>Período</InputLabel>
          <Select
            value={maxDays}
            label="Período"
            onChange={(event) => setMaxDays(event.target.value)}
          >
            {WINDOW_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.select} size="small" variant="outlined">
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={order}
            label="Ordenar por"
            onChange={(event) => setOrder(event.target.value)}
          >
            {ORDER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div className={classes.totals}>
          <div>
            <Typography variant="caption" color="textSecondary" display="block">
              Clientes fora da carteira
            </Typography>
            <Typography variant="h6">{summary.clients}</Typography>
          </div>
          <div>
            <Typography variant="caption" color="textSecondary" display="block">
              Prêmio fora da carteira
            </Typography>
            <Typography variant="h6" className={classes.totalValue}>
              {formatCurrency(summary.lostPremium)}
            </Typography>
          </div>
        </div>
      </Paper>

      <Paper className={classes.paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Produtos</TableCell>
              <TableCell align="center">Última vigência</TableCell>
              <TableCell align="center">Fora há</TableCell>
              <TableCell align="right">Prêmio perdido</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.clientNameNormalized} hover>
                <TableCell>{renderClientCell(client)}</TableCell>
                <TableCell>
                  <div className={classes.products}>
                    {client.products.map((product) => (
                      <Chip key={product} size="small" label={product} variant="outlined" />
                    ))}
                  </div>
                </TableCell>
                <TableCell align="center">{formatDateOnly(client.lastEndDate)}</TableCell>
                <TableCell align="center">{client.daysSinceExpiration} dias</TableCell>
                <TableCell align="right" className={classes.premium}>
                  {formatCurrency(client.lostPremium)}
                  <Typography variant="caption" display="block" color="textSecondary">
                    {client.policyCount} apólice(s)
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<WhatsAppIcon />}
                    disabled={!client.contactId || client.contactPendingPhone}
                    onClick={() => handleOpenTicket(client)}
                  >
                    Conversar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {loading ? <TableRowSkeleton columns={6} /> : null}
            {!loading && clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" style={{ padding: 32 }}>
                  <Typography variant="subtitle1">Nenhum cliente fora da carteira.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        {hasMore && !loading ? (
          <div style={{ textAlign: "center", padding: 8 }}>
            <Button onClick={() => setPageNumber((previous) => previous + 1)}>
              Carregar mais
            </Button>
          </div>
        ) : null}
      </Paper>
    </MainContainer>
  );
};

export default Recovery;
